import React, { useEffect, useState } from "react";
import { Box, Button, Grid, TextField } from "@mui/material";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../services/utils";
import { addGroupSchema, editGroupSchema } from "../../schemas/Group.schema";
import {
  createUserGroup,
  updateUserGroup,
  updateGroupRoles,
  updateGroupAuthTags,
} from "../../services/groupService";
import useFetchGroupById from "../../hooks/useFetchGroupById";
import useFetchGroupRoles from "../../hooks/useFetchGroupRoles";
import useFetchGroupAuthTags from "../../hooks/useFetchGroupAuthTags";
import FlexBetween from "../../components/FlexBetween";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import Loader from "../../components/Molecules/Loader/Loader";
import { AssignRole, AssignTags } from "../../components/UserManagement";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const AddEditGroup: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const { isAdmin } = useAuth();
  const { t } = useAppTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  // State for assignment components
  const [selectedRolesIds, setSelectedRolesIds] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  // State for form changes detection
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [initialFormData, setInitialFormData] = useState<any>({
    groupData: {},
    selectedRolesIds: [],
    selectedTags: [],
  });

  // Fetch group data if editing
  const [groupData] = useFetchGroupById({
    setIsLoading,
    groupId: isEditMode && id ? id : null,
  });

  // Fetch group assignment data if editing
  const [groupAssignedRoles] = useFetchGroupRoles({
    setIsLoading,
    groupId: isEditMode && id ? id : null,
  });

  const [groupAssignedAuthTags] = useFetchGroupAuthTags({
    setIsLoading,
    groupId: isEditMode && id ? id : null,
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
    },
    validationSchema: isEditMode ? editGroupSchema : addGroupSchema,
    enableReinitialize: true,
    onSubmit: async (payload) => {
      try {
        setIsLoading(true);
        let groupId: string | number;

        if (isEditMode) {
          // Update existing group
          await updateUserGroup(id!, payload);
          groupId = id!;
        } else {
          // Create new group
          const newGroup = await createUserGroup(payload);
          groupId = newGroup?.id || newGroup?.group_id;
        }

        // Update group assignments (for both add and edit modes)
        if (groupId) {
          await Promise.all([
            updateGroupRoles(groupId, selectedRolesIds),
            updateGroupAuthTags(groupId, selectedTags),
          ]);
        }

        showToast(
          isEditMode ? t("groups.toast.updated") : t("groups.toast.created"),
          "success"
        );
        navigate("/groups");
      } catch (error: any) {
        showToast(
          error?.message ||
            (isEditMode
              ? t("groups.toast.failedUpdate")
              : t("groups.toast.failedCreate")),
          "error"
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Populate form with group data when editing
  useEffect(() => {
    if (isEditMode && groupData) {
      const values = {
        name: groupData?.name || "",
        description: groupData?.description || "",
      };
      formik.setValues(values);
      setInitialFormData((prev: any) => ({
        ...prev,
        groupData: values,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData, isEditMode]);

  // Initialize assignment selections with fetched data
  useEffect(() => {
    if (isEditMode && groupAssignedRoles?.length > 0) {
      // Extract role IDs from the response
      const roleIds =
        groupAssignedRoles.map((item: any) => item.user_role_id) || [];
      setSelectedRolesIds(roleIds);
      setInitialFormData((prev: any) => ({
        ...prev,
        selectedRolesIds: roleIds,
      }));
    }
  }, [groupAssignedRoles, isEditMode]);

  useEffect(() => {
    if (isEditMode && groupAssignedAuthTags?.length > 0) {
      // Extract tag IDs from the response
      const authTagIds =
        groupAssignedAuthTags.map((item: any) => item.auth_tag_id) || [];
      setSelectedTags(authTagIds);
      setInitialFormData((prev: any) => ({
        ...prev,
        selectedTags: authTagIds,
      }));
    }
  }, [groupAssignedAuthTags, isEditMode]);

  useEffect(() => {
    // Only check for changes if initialFormData is properly set
    if (Object.keys(formik.values).length > 0) {
      const hasFormChanges =
        JSON.stringify(formik.values) !==
          JSON.stringify(initialFormData.groupData) ||
        JSON.stringify(selectedRolesIds) !==
          JSON.stringify(initialFormData.selectedRolesIds) ||
        JSON.stringify(selectedTags) !==
          JSON.stringify(initialFormData.selectedTags);
      setHasChanges(hasFormChanges);
    } else {
      // If initialFormData is not set yet, disable save button
      setHasChanges(false);
    }
  }, [formik.values, initialFormData, selectedRolesIds, selectedTags]);

  // Handler functions for assignment components
  const handleRoleChange = (roleIds: number[]) => {
    console.log("Role Change", roleIds);
    setSelectedRolesIds(roleIds);
  };

  const handleTagChange = (tagIds: number[]) => {
    console.log("Tag Change", tagIds);
    setSelectedTags(tagIds);
  };

  const buildPayload = () => ({
    // Mirrors the payload sent to createUserGroup / updateUserGroup.
    // Role and tag assignments are made via separate API calls
    // (updateGroupRoles, updateGroupAuthTags) and must NOT appear here.
    ...formik.values,
  });

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast(t("groups.toast.payloadCopied"), "success");
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode ? `/user-group/${id}` : "/user-group",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast(t("groups.toast.payloadValid"), "success");
      }
    } catch (error: any) {
      showToast(
        error?.response?.data?.message || t("groups.toast.validationFailed"),
        "error"
      );
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
              <label className="label-text">
                {t("groups.form.groupName")}
                <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.name && formik.errors.name ? "has-error" : ""
                }`}
                error={!!(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                value={formik.values.name}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
              <label className="label-text">
                {t("groups.form.description")}
              </label>
              <TextField
                type="text"
                name="description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.description && formik.errors.description
                    ? "has-error"
                    : ""
                }`}
                error={
                  !!(formik.touched.description && formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                value={formik.values.description}
              />
            </Grid>

            {/* Assignment Components */}
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <AssignRole
                selectedRolesIds={selectedRolesIds}
                onRoleChange={handleRoleChange}
                disabled={isLoading}
                label={t("groups.form.assignRoles")}
                placeholder={t("groups.form.placeholder")}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <AssignTags
                selectedTags={selectedTags}
                onTagChange={handleTagChange}
                disabled={isLoading}
                label={t("groups.form.assignTags")}
                placeholder={t("groups.form.placeholder")}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            {/* <Button
              type="button"
              color="secondary"
              variant="contained"
              onClick={() => navigate("/groups")}
              className="btn-orange btn-dark"
            >
              Cancel
            </Button> */}
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isEditMode ? !hasChanges : false}
            >
              <SaveOutlinedIcon /> &nbsp;{" "}
              {isEditMode ? t("groups.form.update") : t("groups.form.save")}
            </Button>
          </FlexBetween>
        </Box>
      </form>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle={t("groups.form.jsonDialogTitle")}
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddEditGroup;
